import { useEffect, useState } from 'react';
import { Download, ImageOff, Images, RefreshCw } from 'lucide-react';
import type { AspectRatio, MediaGenerationView } from '@mg/shared';
import { ratioParts } from '@mg/shared';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Field';

/**
 * Progress text.
 *
 * DELIBERATELY FAKED, and permanently so: no provider exposes generation progress — the async
 * task state is a three-value enum and the sync ones return only when finished. Client-side timed
 * stages are the only implementation that will ever exist, so this reads as reassurance rather
 * than measurement, and the honest "usually 10–20 seconds" sits underneath.
 */
const STAGES = ['Queued…', 'Composing…', 'Rendering…', 'Finishing…'];

export function GenerationPreview({
  status,
  error,
  active,
  versions,
  activeIndex,
  onSelectVersion,
  specSummary,
  aspect,
  changeRequest,
  onChangeRequest,
  canRefine,
  onUpdate,
  onRegenerate,
  onViewInLibrary,
  onRetry,
}: {
  status: 'idle' | 'generating' | 'done' | 'error';
  error: string | null;
  active: MediaGenerationView | null;
  versions: MediaGenerationView[];
  activeIndex: number;
  onSelectVersion: (i: number) => void;
  specSummary: string;
  aspect: AspectRatio;
  changeRequest: string;
  onChangeRequest: (v: string) => void;
  canRefine: boolean;
  onUpdate: () => void;
  onRegenerate: () => void;
  onViewInLibrary: () => void;
  onRetry: () => void;
}) {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (status !== 'generating') {
      setStage(0);
      return;
    }
    const timer = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 4000);
    return () => clearInterval(timer);
  }, [status]);

  // `auto` has no ratio, so the frame falls back to square — matching what the model is told.
  const parts = ratioParts(aspect) ?? { w: 1, h: 1 };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-baseline justify-between gap-2 border-b border-line-soft px-5 py-3">
        <h3 className="text-sm font-semibold text-ink">Preview</h3>
        <p className="truncate text-xs text-ink-faint">{specSummary}</p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div
          className="mx-auto flex w-full max-w-md items-center justify-center overflow-hidden rounded-xl border border-line bg-page"
          style={{ aspectRatio: `${parts.w} / ${parts.h}` }}
        >
          {status === 'done' && active ? (
            <GeneratedImage id={active.id} />
          ) : status === 'generating' ? (
            <div className="flex flex-col items-center gap-2 text-center">
              <span className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-action" />
              <p className="text-sm font-medium text-ink-strong">{STAGES[stage]}</p>
              <p className="text-xs text-ink-faint">Usually 10–20 seconds.</p>
            </div>
          ) : status === 'error' ? (
            <div className="max-w-xs px-4 text-center">
              <ImageOff className="mx-auto h-7 w-7 text-red-400 dark:text-error-dark" aria-hidden />
              <p className="mt-2 text-sm text-red-600 dark:text-error-dark">{error}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
                Retry
              </Button>
            </div>
          ) : (
            <div className="max-w-xs px-4 text-center">
              <p className="text-sm font-medium text-ink-label">Nothing generated yet</p>
              <p className="mt-1 text-xs text-ink-faint">
                Your image appears here when it&apos;s ready, and is added to your Media Library
                automatically. The modal stays open so you can keep refining.
              </p>
            </div>
          )}
        </div>

        {status === 'done' && active && (
          <div className="mx-auto mt-4 w-full max-w-md space-y-3">
            {versions.length > 1 && (
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Versions
                </p>
                <ul className="flex flex-wrap gap-2">
                  {versions.map((v, i) => (
                    <li key={v.id}>
                      <button
                        type="button"
                        onClick={() => onSelectVersion(i)}
                        title={v.changeRequest || v.prompt}
                        aria-pressed={i === activeIndex}
                        className={cn(
                          'focus-ring h-14 w-14 overflow-hidden rounded-lg border',
                          i === activeIndex
                            ? 'border-action ring-2 ring-action/20'
                            : 'border-line hover:border-ink-ghost',
                        )}
                      >
                        <GeneratedImage id={v.id} thumb />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {canRefine && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Request changes
                </label>
                <div className="flex gap-2">
                  <Input
                    value={changeRequest}
                    onChange={(e) => onChangeRequest(e.target.value)}
                    placeholder="e.g. Warmer lighting, remove the text in the corner"
                  />
                  <Button size="sm" disabled={!changeRequest.trim()} onClick={onUpdate}>
                    Update
                  </Button>
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      {/*
        Pinned to the bottom of the COLUMN, outside the scroller — the same treatment the request
        column's Cancel/Generate bar gets. These three were the last thing in a scrolling area, so
        on a tall result they sat below the fold: the actions on a finished image were the part you
        had to go looking for.
      */}
      {status === 'done' && active && (
        <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center gap-2 border-t border-line-soft bg-card px-5 py-3 md:static">
          <Button variant="secondary" size="sm" onClick={onRegenerate}>
            <RefreshCw className="h-4 w-4" aria-hidden />
            Regenerate
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void downloadGeneration(active)}>
            <Download className="h-4 w-4" aria-hidden />
            Download
          </Button>
          <Button size="sm" onClick={onViewInLibrary}>
            <Images className="h-4 w-4" aria-hidden />
            View in Media Library
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The bytes live behind an auth-gated route, so a bare `<img src>` would 401. Fetch with the
 * bearer token and render an object URL, revoking it when the version changes.
 */
function GeneratedImage({ id, thumb }: { id: string; thumb?: boolean }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    void api
      .fetchGenerationImage(id)
      .then((blob) => {
        if (!blob || revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  if (!url) {
    return <span className={cn('block animate-pulse bg-chip', thumb ? 'h-full w-full' : 'h-full w-full')} />;
  }
  return <img src={url} alt="" className="h-full w-full object-contain" />;
}

async function downloadGeneration(row: MediaGenerationView): Promise<void> {
  const blob = await api.fetchGenerationImage(row.id);
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${row.prompt.slice(0, 40).replace(/[^a-z0-9]+/gi, '-') || 'generated'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
